import React, { useEffect, useState } from "react";
import { AxiosResponse } from "axios";

import { PreviewDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { dinero, errorText } from "./comun";

interface Persona { nombre: string; lineas: { t: string; oro?: boolean }[]; }

/** Arma el resumen por persona con lo que el servidor dice que va a cambiar (nada se calcula aquí). */
export const personasDe = (pv: PreviewDTO): Persona[] => {
	const m = (v: number) => dinero(pv.currency, v);
	const map = new Map<string, Persona>();
	const de = (nombre: string): Persona => {
		if (!map.has(nombre)) map.set(nombre, { nombre, lineas: [] });
		return map.get(nombre) as Persona;
	};
	const hostName = (id: number | null | undefined) => (id ? pv.hosts.find(h => h.hostAttendeeId === id)?.name : null) || null;
	pv.hosts.forEach(h => de(h.name));
	if (pv.action === "anular") {
		const p = de("");
		p.lineas.push({ t: S.qvpAnularEvento });
		p.lineas.push({ t: pv.cancelled.length ? S.qvpAnularCobros(pv.cancelled.length, m(pv.cancelledTotal)) : S.qvpAnularSinCobros });
		if (pv.invitesSent) p.lineas.push({ t: S.qvpAnularInvitaciones(pv.invitesSent) });
		return [p];
	}
	const nuevos = new Set(pv.added.map(a => a.attendeeId));
	const recalculados = new Set(pv.repriced.map(r => r.attendeeId));
	pv.added.forEach(a => de(hostName(a.hostAttendeeId) || a.name).lineas.push({ t: S.qvpSeAgrega(a.name, a.amount ? m(a.amount) : null) }));
	pv.created.forEach(c => {
		if (c.kind === "asistente" && c.attendeeId && (nuevos.has(c.attendeeId) || recalculados.has(c.attendeeId))) return;
		de(c.studentName || c.name || "").lineas.push({ t: S.qvpCargo(c.name || c.studentName || "", c.concept || c.title, m(c.amount)) });
	});
	pv.repriced.forEach(r => de(r.hostName || r.name).lineas.push({ t: S.qvpPrecio(r.name, r.from ? m(r.from) : S.gratisPaquete, r.to ? m(r.to) : S.gratisPaquete), oro: r.to > r.from }));
	pv.cancelled.forEach(c => {
		if (c.attendeeId && recalculados.has(c.attendeeId)) return;
		de(c.studentName || c.name || "").lineas.push({ t: S.qvpAnula(c.concept || c.name || c.title, m(c.amount)) });
	});
	pv.hosts.forEach(h => {
		const p = de(h.name);
		if (pv.action === "attendees" && h.guestsAfter !== h.guestsBefore) p.lineas.push({ t: S.qvpPaquete(h.guestsBefore, h.guestsAfter) });
		if (h.pendingAfter !== h.pendingBefore) {
			if (pv.action !== "update") p.lineas.push({ t: S.qvpPagadoNoSeToca });
			p.lineas.push({ t: S.qvpFaltara(m(h.pendingAfter)), oro: h.pendingAfter > 0.004 });
		}
	});
	const out = Array.from(map.values()).filter(p => p.lineas.length > 0);
	return out.length ? out : [{ nombre: "", lineas: [{ t: S.qvpSinCambios }] }];
};

/** Cuerpo del «Qué Va a Pasar»: pide la vista previa al servidor, la muestra por persona y deja confirmar solo si se puede. */
export const QvpCuerpo: React.FC<{ pv: PreviewDTO; titulo?: string }> = ({ pv, titulo }) => {
	const m = (v: number) => dinero(pv.currency, v);
	return (
		<div className="wevQvp">
			<h6>{titulo || S.qvp}</h6>
			{pv.ok ? personasDe(pv).map((p, i) => (
				<div className="persona" key={i}>
					{p.nombre ? <b>{p.nombre}</b> : null}
					<ul>{p.lineas.map((l, j) => <li key={j} className={l.oro ? "wevOro" : undefined}>{l.t}</li>)}</ul>
				</div>
			)) : <>
				<div className="error">{pv.error}</div>
				{pv.withPayments?.length ? <ul style={{ marginTop: 6 }}>{pv.withPayments.map((w, i) => <li key={i}>{w.name}{w.concept ? " · " + w.concept : ""}: {S.qvpConAbonos} {m(w.paid)} / {m(w.total)}</li>)}</ul> : null}
			</>}
			{pv.ok && pv.chargedTotal > 0.004 && pv.action !== "update" ? <div style={{ marginTop: 6, fontWeight: 700 }}>{S.qvpTotal(m(pv.chargedTotal))}</div> : null}
		</div>
	);
};

interface Props {
	campusId: number;
	pedir: () => Promise<AxiosResponse<PreviewDTO>>;
	confirmar: string;
	busy?: boolean;
	titulo?: string;
	onVolver: () => void;
	onConfirmar: () => void;
	/** Para quien quiere saber si hubo cambios de dinero (Editar guarda directo si no los hay). */
	onCargado?: (pv: PreviewDTO) => void;
}

const QueVaAPasar: React.FC<Props> = ({ pedir, confirmar, busy, titulo, onVolver, onConfirmar, onCargado }) => {
	const [pv, setPv] = useState<PreviewDTO | null>(null);
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		let vigente = true;
		pedir().then(r => { if (vigente) { setPv(r.data); onCargado?.(r.data); } }).catch(e => { if (vigente) setError(errorText(e)); });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return (
		<div>
			{pv ? <QvpCuerpo pv={pv} titulo={titulo} /> : <div className="wevQvp"><h6>{titulo || S.qvp}</h6>{error ? <div className="error">{error}</div> : <div className="wevSub">{S.qvpCalculando}</div>}</div>}
			<div className="wevQvp" style={{ border: 0, background: "none", padding: 0, marginTop: 0 }}>
				<div className="acciones">
					<button type="button" className="btn btn-outline-secondary btn-sm" onClick={onVolver} disabled={busy}>{S.volver}</button>
					<button type="button" className="btn btn-primary btn-sm" onClick={onConfirmar} disabled={busy || !pv || !pv.ok}>{busy ? S.guardando : confirmar}</button>
				</div>
			</div>
		</div>
	);
};

export default QueVaAPasar;
